import cx from 'clsx';
import dayjs from 'dayjs';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { Icon, Tag } from '@vechaiui/react';
import { ShieldCheckIcon } from '@heroicons/react/solid';
import { useEffect, useRef } from 'react';
import { useLocalization } from '@fluent/react';
import { useSearchParams } from 'react-router-dom';

import Timeline, { TimelineItem } from '#/components/display/Timeline';
import { FragmentType, getFragmentData } from '#/__generated__/graphql';
import { OrderCardFragmentDoc, RouteLegDetailsFragmentDoc } from '#/__generated__/graphql/graphql';
import { STATE_LIST } from '#/constants/malaysia-state-list';
import { SearchParam } from '#/constants/search-params';
import { formatMoney } from '#/utils/formatter';
import { getColorByStatus } from '#/utils/order-status';

const OrderCard: React.FC<OrderCardProps> = ({ order: _order }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { l10n } = useLocalization();
  const ref = useRef<HTMLDivElement>(null);

  const order = getFragmentData(OrderCardFragmentDoc, _order);
  const routeLegs = order.acceptedOffer.routeLegs?.map((leg) =>
    getFragmentData(RouteLegDetailsFragmentDoc, leg),
  );

  const isActive = searchParams.get(SearchParam.OrderNumber) === order.orderNumber;

  const handleClick = () => {
    setSearchParams((sp) => {
      sp.set(SearchParam.OrderNumber, order.orderNumber);
      return sp;
    });
  };

  // FIXME: should a dumb component has smart feature?
  useEffect(() => {
    if (isActive) {
      ref.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  }, [isActive]);

  return (
    <div
      className={cx(
        'bg-white rounded-2xl px-4 hover:shadow-xl transition-shadow divide-y 2xl:w-96 border border-neutral-300',
        isActive && 'shadow-xl border-primary-200',
      )}
      onClick={handleClick}
      ref={ref}
    >
      <div className="py-3 space-y-4">
        <div className="flex">
          <div className="flex-1">
            <span className="text-lg font-bold uppercase">
              {l10n.getString('id')} #{order.orderNumber}
            </span>
            {order.acceptedOffer.cargoDetails.description && (
              <p className="text-sm text-neutral-400">
                {order.acceptedOffer.cargoDetails.description}
              </p>
            )}
          </div>

          <div>
            <Tag
              color={getColorByStatus(order.orderStatus)}
              variant="solid"
              className="uppercase rounded-lg tracking-wider font-bold"
            >
              {l10n.getString(order.orderStatus)}
            </Tag>
          </div>
        </div>

        <div className="flex">
          <div className="flex-1">
            <Timeline>
              {routeLegs.map((routeLeg, index) => (
                <TimelineItem
                  key={index}
                  secondaryText={dayjs(order.acceptedOffer.pickupTime).format('lll')}
                >
                  {routeLeg.startLocation.address.addressLocality}
                  {', '}
                  {
                    STATE_LIST.find((o) => o.code === routeLeg.startLocation.address.addressRegion)
                      ?.name
                  }
                </TimelineItem>
              ))}
              <TimelineItem secondaryText={dayjs(order.acceptedOffer.deliveryTime).format('lll')}>
                {routeLegs[routeLegs.length - 1].endLocation.address.addressLocality}
                {', '}
                {
                  STATE_LIST.find(
                    (o) =>
                      o.code === routeLegs[routeLegs.length - 1].endLocation.address.addressRegion,
                  )?.name
                }
              </TimelineItem>
            </Timeline>
          </div>

          <div className="self-end mb-2">
            <Icon as={ArrowRightIcon} className="w-5 text-neutral-300" label="selector" />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 py-3">
        <div>
          <span className="text-lg font-bold">{formatMoney(order.orderTotal)}</span>
        </div>

        <div className="flex gap-1">
          {/* <Icon as={CheckCircleIcon} className={cx('w-5 text-green-500 ')} label="check" /> */}
          {order.acceptedOffer.insuranceIncluded && (
            <Icon as={ShieldCheckIcon} className={cx('w-5 text-primary-500')} label="insurance" />
          )}
        </div>
      </div>
    </div>
  );
};
export type OrderCardProps = {
  order: FragmentType<typeof OrderCardFragmentDoc>;
};

export default OrderCard;
